/**
 * URL state of the Finance screen (state priority 1: search params). The shell keeps `period`
 * and `issuer`; these keys hold the tab, the report year and the matrix view controls. Default
 * values are left out of the URL.
 *
 *   /finance?tab=cash&year=2025&show=margin_pct&group=issuer&ccy=native&cell=heat&tax_issuer=<id>
 */
import type { SearchSchemaInput } from '@tanstack/react-router';
import {
  FINANCE_CURRENCY_MODES,
  FINANCE_GROUP_BY,
  MAX_FINANCE_YEAR,
  MIN_FINANCE_YEAR,
  PNL_METRICS,
  type FinanceCurrencyMode,
  type FinanceExportView,
  type FinanceGroupBy,
  type PnlMetric,
} from '@outsource/shared/schemas/finance';

export const FINANCE_TABS = ['matrix', 'cash', 'ageing', 'tax'] as const;
export type FinanceTab = (typeof FINANCE_TABS)[number];

/** Cell style of the matrix (design F:165). */
export const CELL_STYLES = ['stacked', 'heat', 'plain'] as const;
export type CellStyle = (typeof CELL_STYLES)[number];

/** Export `view` per tab. */
export const EXPORT_VIEW: Readonly<Record<FinanceTab, FinanceExportView>> = {
  matrix: 'pnl',
  cash: 'cash',
  ageing: 'receivables',
  tax: 'tax',
};

export interface FinanceSearch {
  readonly tab?: Exclude<FinanceTab, 'matrix'>;
  readonly year?: number;
  readonly show?: Exclude<PnlMetric, 'billed'>;
  readonly group?: Exclude<FinanceGroupBy, 'client'>;
  readonly ccy?: Exclude<FinanceCurrencyMode, 'base'>;
  readonly cell?: Exclude<CellStyle, 'stacked'>;
  readonly tax_issuer?: string;
}

/** Every Finance view setting with its default filled in (`year` stays optional). */
export interface FinanceView {
  readonly tab: FinanceTab;
  readonly year?: number;
  readonly metric: PnlMetric;
  readonly groupBy: FinanceGroupBy;
  readonly currency: FinanceCurrencyMode;
  readonly cellStyle: CellStyle;
  readonly taxIssuer?: string;
}

export const DEFAULT_VIEW = {
  tab: 'matrix',
  metric: 'billed',
  groupBy: 'client',
  currency: 'base',
  cellStyle: 'stacked',
} as const satisfies Omit<FinanceView, 'year' | 'taxIssuer'>;

const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

function oneOf<T extends string>(list: readonly T[], value: unknown, fallback: T): T | undefined {
  if (typeof value !== 'string' || !(list as readonly string[]).includes(value)) return undefined;
  return value === fallback ? undefined : (value as T);
}

function asYear(value: unknown): number | undefined {
  const year = typeof value === 'string' ? Number(value) : value;
  if (typeof year !== 'number' || !Number.isInteger(year)) return undefined;
  return year >= MIN_FINANCE_YEAR && year <= MAX_FINANCE_YEAR ? year : undefined;
}

function asId(value: unknown): string | undefined {
  const text = typeof value === 'number' ? String(value) : value;
  return typeof text === 'string' && ID_RE.test(text) ? text : undefined;
}

type SearchFields = { readonly [K in keyof FinanceSearch]?: unknown };

/** Keeps the well-formed Finance keys; malformed or default values are dropped. */
export function parseFinanceSearch(input: SearchFields): FinanceSearch {
  const entries: Array<[string, unknown]> = [
    ['tab', oneOf(FINANCE_TABS, input.tab, DEFAULT_VIEW.tab)],
    ['year', asYear(input.year)],
    ['show', oneOf(PNL_METRICS, input.show, DEFAULT_VIEW.metric)],
    ['group', oneOf(FINANCE_GROUP_BY, input.group, DEFAULT_VIEW.groupBy)],
    ['ccy', oneOf(FINANCE_CURRENCY_MODES, input.ccy, DEFAULT_VIEW.currency)],
    ['cell', oneOf(CELL_STYLES, input.cell, DEFAULT_VIEW.cellStyle)],
    ['tax_issuer', asId(input.tax_issuer)],
  ];
  return Object.fromEntries(entries.filter(([, value]) => value !== undefined)) as FinanceSearch;
}

/** Route `validateSearch`: unknown or malformed values are dropped, never an error. */
export function validateFinanceSearch(input: SearchFields & SearchSchemaInput): FinanceSearch {
  return parseFinanceSearch(input);
}

export function viewOf(search: FinanceSearch): FinanceView {
  return {
    tab: search.tab ?? DEFAULT_VIEW.tab,
    year: search.year,
    metric: search.show ?? DEFAULT_VIEW.metric,
    groupBy: search.group ?? DEFAULT_VIEW.groupBy,
    currency: search.ccy ?? DEFAULT_VIEW.currency,
    cellStyle: search.cell ?? DEFAULT_VIEW.cellStyle,
    taxIssuer: search.tax_issuer,
  };
}

/** A view change as a search patch: defaults become `undefined` so they leave the URL. */
export function searchPatchOf(change: Partial<FinanceView>): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if ('tab' in change) patch.tab = change.tab === DEFAULT_VIEW.tab ? undefined : change.tab;
  if ('year' in change) patch.year = change.year;
  if ('metric' in change) patch.show = change.metric === DEFAULT_VIEW.metric ? undefined : change.metric;
  if ('groupBy' in change) patch.group = change.groupBy === DEFAULT_VIEW.groupBy ? undefined : change.groupBy;
  if ('currency' in change)
    patch.ccy = change.currency === DEFAULT_VIEW.currency ? undefined : change.currency;
  if ('cellStyle' in change)
    patch.cell = change.cellStyle === DEFAULT_VIEW.cellStyle ? undefined : change.cellStyle;
  if ('taxIssuer' in change) patch.tax_issuer = change.taxIssuer;
  return patch;
}
